import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Modal, Select, Space, Table, Tag, Upload } from 'antd';
import { DownloadOutlined, InboxOutlined } from '@ant-design/icons';
import { API } from '@/services/api.service';
import { errorText, money, saveBlob } from './format';

const ACCEPT = '.xlsx,.xls,.csv';
const NONE = -1;

/**
 * A client's sheet of people (name, JMBG / passport, net) brought into a
 * calculation. The server reads it and checks every row; the accountant sees
 * which column was taken as what (and can pick another), which rows have an
 * error, and who is already in the company's list. Confirming puts the good
 * rows' people into the list (linked when already there, never twice) and
 * hands the rows to the form, after what is already in it.
 *
 * @param {string} kind - the calculation's kind, the people are listed for it
 * @param {string} currency - the calculation's currency, the sheet's net is in it
 * @param {string[]} existingIds - JMBGs / passports already in the calculation, not added again
 * @param {(rows: object[], summary: { created: number, linked: number }) => void} onImported
 */
export default function RecipientSheetModal({ open, company, kind, currency, existingIds = [], onClose, onImported }) {
  const { t, i18n } = useTranslation('taxReturns');
  const queryClient = useQueryClient();
  const [file, setFile] = useState(null);
  const [mapping, setMapping] = useState(null);

  useEffect(() => {
    if (!open) {
      setFile(null);
      setMapping(null);
    }
  }, [open]);

  const preview = useMutation({
    mutationFn: ({ file: f, mapping: m }) => API.taxReturns.previewRecipientSheet(company, f, m),
    onSuccess: (data) => setMapping(data.mapping),
  });

  const template = useMutation({
    mutationFn: () => API.taxReturns.getRecipientSheetTemplate(i18n.language),
    onSuccess: ({ blob, fileName }) => saveBlob(blob, fileName),
  });

  const confirm = useMutation({
    mutationFn: (rows) =>
      API.taxReturns.importRecipients(
        company,
        kind,
        rows.map((r) => ({ name: r.name, idNumber: r.idNumber }))
      ),
    onSuccess: (result, rows) => {
      queryClient.invalidateQueries({ queryKey: ['tax-recipients', company] });
      onImported(
        result.items.map((p, i) => ({ ...p, netPerMonth: rows[i].net })),
        { created: result.created, linked: result.linked }
      );
      onClose();
    },
  });

  const pick = (f) => {
    setFile(f);
    setMapping(null);
    preview.mutate({ file: f, mapping: null });
  };

  const changeRole = (role, value) => {
    const next = { ...mapping, [role]: value === NONE ? null : value };
    setMapping(next);
    preview.mutate({ file, mapping: next });
  };

  const { data } = preview;
  const taken = useMemo(() => new Set(existingIds.filter(Boolean)), [existingIds]);
  // The calculation already has this person: not added a second time.
  const rows = useMemo(
    () =>
      (data?.rows || []).map((r) => ({
        ...r,
        errors: r.idNumber && taken.has(r.idNumber) && !r.errors.length ? ['already_in_calc'] : r.errors,
      })),
    [data, taken]
  );
  const good = rows.filter((r) => !r.errors.length);
  const bad = rows.length - good.length;
  const known = good.filter((r) => r.existing).length;
  const netTitle = t('calc.fields.netPerMonth', { currency: currency || 'EUR' });

  const columnOptions = [
    { value: NONE, label: t('calc.sheet.noColumn') },
    ...(data?.columns || []).map((c) => ({ value: c.index, label: c.label })),
  ];

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width={980}
      maskClosable={false}
      destroyOnClose
      title={t('calc.sheet.title')}
      footer={
        <Space>
          <Button shape="round" onClick={onClose}>
            {t('calc.sheet.cancel')}
          </Button>
          <Button
            shape="round"
            type="primary"
            disabled={!good.length || preview.isPending}
            loading={confirm.isPending}
            onClick={() => confirm.mutate(good)}
          >
            {t('calc.sheet.confirm', { count: good.length })}
          </Button>
        </Space>
      }
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <p className="text-sm opacity-70">{t('calc.sheet.help', { currency: currency || 'EUR' })}</p>
        <Space wrap>
          <Upload
            accept={ACCEPT}
            showUploadList={false}
            beforeUpload={(f) => {
              pick(f);
              return false;
            }}
          >
            <Button shape="round" icon={<InboxOutlined />} loading={preview.isPending && !data}>
              {file ? t('calc.sheet.otherFile') : t('calc.sheet.pickFile')}
            </Button>
          </Upload>
          <Button shape="round" icon={<DownloadOutlined />} loading={template.isPending} onClick={() => template.mutate()}>
            {t('calc.sheet.template')}
          </Button>
          {file && <span className="text-sm">{file.name}</span>}
        </Space>

        {(preview.isError || template.isError || confirm.isError) && (
          <Alert type="error" showIcon message={errorText(t, preview.error || template.error || confirm.error)} />
        )}

        {data && mapping && (
          <>
            <div>
              <div className="mb-1 text-sm font-medium">{t('calc.sheet.columns')}</div>
              <Space wrap>
                {data.roles.map((role) => (
                  <Space key={role} size={4}>
                    <span className="text-sm">{t(`calc.sheet.roles.${role}`)}:</span>
                    <Select
                      size="small"
                      style={{ width: 170 }}
                      value={mapping[role] ?? NONE}
                      options={columnOptions}
                      onChange={(v) => changeRole(role, v)}
                    />
                  </Space>
                ))}
              </Space>
            </div>
            <Alert
              type={bad ? 'warning' : 'success'}
              showIcon
              message={t('calc.sheet.summary', { good: good.length, known, bad, skipped: data.skipped })}
            />
            <Table
              size="small"
              rowKey="row"
              dataSource={rows}
              loading={preview.isPending}
              pagination={{ pageSize: 50, hideOnSinglePage: true }}
              scroll={{ x: true }}
              columns={[
                { title: t('calc.sheet.row'), dataIndex: 'row', width: 60, align: 'right' },
                { title: t('calc.fields.recipient'), dataIndex: 'name', render: (v) => v || '—' },
                {
                  title: t('calc.fields.idNumber'),
                  dataIndex: 'idNumber',
                  render: (v, r) =>
                    v ? (
                      <Space size={4}>
                        <Tag>{t(`calc.sheet.idTypes.${r.idType}`)}</Tag>
                        {v}
                      </Space>
                    ) : (
                      '—'
                    ),
                },
                {
                  title: netTitle,
                  dataIndex: 'net',
                  align: 'right',
                  render: (v, r) => (v === null ? r.netText || '—' : money(v)),
                },
                {
                  title: t('calc.sheet.status'),
                  render: (r) =>
                    r.errors.length ? (
                      <Space size={4} wrap>
                        {r.errors.map((e) => (
                          <Tag key={e} color="red">
                            {t(`calc.sheet.rowErrors.${e}`)}
                          </Tag>
                        ))}
                      </Space>
                    ) : r.existing ? (
                      <Tag color="blue">{t('calc.sheet.existing', { name: r.existing.name })}</Tag>
                    ) : (
                      <Tag color="green">{t('calc.sheet.new')}</Tag>
                    ),
                },
              ]}
            />
          </>
        )}
      </Space>
    </Modal>
  );
}
